import { useMemo } from 'react';

import { isSmallProjectColor, isSmallProjectFee } from '../helpers';

import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { SMALL_JOB_AREA } from '~/core/constants';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { useShapeArea } from '~/core/hooks/useShapeArea';
import { ProductDetails } from '~/core/models';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectValidSmallTicketNumber } from '~/redux/slices/estimate/estimate.selectors';
import { useProductDetailsContext } from '~contexts';

export interface UseSmallJobsReturn {
  /**
   * This is the value to be used to both test for "is a small job" and also the minimum area
   * that will be charged for small jobs unless there's an override.
   */
  smallJobMinimumArea: number;

  /**
   * This product for the fee that will be charge for fee-based small jobs.
   * If this is non-null, then colors tagged for small job use will add this to the project if the area is within range
   * It is the first product in the catalog that matches the filter ProductFilters.smallProjectFee
   */
  smallJobFeeProduct: ProductDetails | undefined;

  /**
   * Does the estimate qualify as a small job? This is the broad definition, it may also be a smallJobWithFee,
   * which takes priority.
   */
  isSmallJob: boolean;

  /**
   * Does this estimate qualify as a small job, with isSmallJobColor and smallJobFeeProduct exists
   */
  isSmallJobWithFee: boolean; // true if it's a small job and there's a fee product available

  /**
   * Is the currently selected color eligible for a small-project fee?
   */
  isSmallJobColor: boolean;

  /**
   * Should we use the actual area for pricing?
   * True if it's a small-job-with-fee OR if it's a small job and has an override code
   * This is the inverse of useMinQtyAreaForPricing
   */

  useActualAreaForPricing: boolean;

  /**
   * Should we use the defined minimum quantity area for pricing?
   * This is the inverse of useActualAreaForPricing
   */
  useMinQtyAreaForPricing: boolean;

  /**
   * Should the small job minimum quantity override be shown?
   */
  canOverrideSmallJob: boolean;
}

export const useSmallJobs = (): UseSmallJobsReturn => {
  const validatedTicketNumber = useAppSelector(selectValidSmallTicketNumber);
  const selectedColor = useAppSelector(selectSelectedColor);
  const selectedMaterial = useSelectedMaterial();
  const { totalMaterialArea = 0 } = useShapeArea();
  const { productCatalog } = useProductDetailsContext();
  const { retailerSettings } = useRetailerSettings();

  /**
   * This product is the fee that will be charge for small jobs (>10 && <25) IF it's a smallJobColor
   * It is the first product in the catalog that matches the filter ProductFilters.smallProjectFee
   */
  const smallJobFeeProduct = useMemo(
    () => Object.values(productCatalog).find(isSmallProjectFee),
    [productCatalog]
  );

  /**
   * If the selectedColor is identified to be a small job color, then it can be used in small jobs
   * but the smallJobFeeProduct will be added to the estimate.
   */
  const isSmallJobColor = useMemo(
    () => !!selectedColor && isSmallProjectColor(selectedColor),
    [selectedColor]
  );

  /**
   * this is the maximum area allowed to qualify as a small job
   */
  const smallJobThreshold =
    selectedMaterial?.installedMinSqFt ?? SMALL_JOB_AREA.DEFAULT_HIGH;

  /**
   * Does this qualify as a generic "small job"
   */
  const isSmallJob =
    totalMaterialArea > 0 && totalMaterialArea < smallJobThreshold;

  /**
   * Is this s small job color AND there's an available small job fee?
   */
  const isSmallJobFeeEligible =
    totalMaterialArea >= SMALL_JOB_AREA.LOW &&
    !!smallJobFeeProduct &&
    isSmallJobColor;

  /**
   * this is the minimum area allowed before a job qualifies as a small job
   */
  const smallJobMinimumArea =
    selectedMaterial?.installedMinSqFt ?? SMALL_JOB_AREA.DEFAULT_HIGH;

  /**
   * If it's a smallJob by area, smallJobColor, a smallJobFeeProduct is available
   * then this will get a small job fee product added to the estimate, and we should use the actual area for pricing
   */
  const isSmallJobWithFee = isSmallJob && isSmallJobFeeEligible;

  /**
   * Calculations to test if the small job fee costs more than the minimium sqft
   */

  /**
   * Use actual area if
   * * it's not a small job, OR
   * * it's a small job with fee
   * * we have a valid ticket number
   */
  const useActualAreaForPricing =
    !isSmallJob || isSmallJobWithFee || !!validatedTicketNumber;

  const canOverrideSmallJob = useMemo(
    () =>
      !!retailerSettings?.confirmSmallProjectOverride &&
      isSmallJob &&
      !isSmallJobWithFee,
    [
      isSmallJob,
      isSmallJobWithFee,
      retailerSettings?.confirmSmallProjectOverride,
    ]
  );

  return {
    smallJobMinimumArea,
    smallJobFeeProduct,
    useMinQtyAreaForPricing: !useActualAreaForPricing,
    isSmallJob,
    isSmallJobColor,
    useActualAreaForPricing,
    isSmallJobWithFee,
    canOverrideSmallJob,
  };
};
